import { pure, raise, toActionObject } from 'xstate/lib/actions'
import { assign } from 'xstate'
import { ACTION, STEP } from '../@types/state'
import { FlowMachineContext } from './flowMachine'
import { reportEventSync } from '../tracking/tracking'
import { TimeHorizon } from '../@types/timeHorizon'
import { isOwnerOrPartialOwner } from '../utils/ownership'
import { getSvcModuleName } from '../tracking/thunks'
import { IntegrationType } from '../@types/config'
import { extendedBuyerLeadPostcodes } from '../../public/static/PLZ_BuyerLeads_EV'
import { getFlowSkippedSteps } from '../utils/skipStepsHandler'

const sendRichTracking = () => {
  return pure((ctx: FlowMachineContext, _) => {
    const { lead, wizardFlowType, richReportSent, tealium, integrationType } = ctx
    const { timeHorizon, ownership } = lead || {}
    const svcModuleName = getSvcModuleName(integrationType === IntegrationType.EXTERNAL, wizardFlowType)
    if (richReportSent || !tealium) return assign({})
    if (
      (timeHorizon == TimeHorizon.IMMEDIATELY || timeHorizon == TimeHorizon.NEXT_SIX_MONTHS) &&
      isOwnerOrPartialOwner(ownership)
    ) {
      reportEventSync({
        label: 'rich',
        action: 'homeowner',
        category: 'lead',
        pageTitle: 'rich',
        inputValue: '',
        eventName: 'lead_submit',
        customDimensions: {
          svc_module_name: svcModuleName,
        },
      })
    } else {
      reportEventSync({
        label: 'request_rich',
        action: 'realtor-leadengine',
        category: 'residential',
        pageTitle: 'request_rich',
        inputValue: '',
        eventName: 'lead_submit',
        customDimensions: {
          svc_module_name: svcModuleName,
        },
      })
    }
  })
}

const skipStep = (step: STEP) => {
  return pure((context: FlowMachineContext, event) => {
    if (context.skippedSteps && context.skippedSteps.includes(step))
      return event.type == ACTION.BACK ? raise(event) : raise(ACTION.NEXT)
  })
}

export const extendedBuyerLeadPostcodesList = (context: FlowMachineContext) =>
  extendedBuyerLeadPostcodes.toString().includes(context.location?.postcode as string)

export const flowMachineActions = {
  enterOrSkipRealEstateType: toActionObject(skipStep(STEP.REAL_ESTATE_TYPE)),
  enterOrSkipRooms: toActionObject(skipStep(STEP.ROOMS)),
  enterOrSkipArea: toActionObject(skipStep(STEP.AREA)),
  enterOrSkipLocation: toActionObject(skipStep(STEP.LOCATION)),
  enterOrSkipIntent: toActionObject(skipStep(STEP.INTENT)),
  enterOrSkipBuyerLeadsProposition: toActionObject(skipStep(STEP.BUYER_LEADS_VALUE_PROPOSITION)),
  enterOrSkipDevComOffer: toActionObject(skipStep(STEP.DEV_COM_OFFER)),
  enterOrSkipSellIntent: toActionObject(skipStep(STEP.SELL_INTENT)),
  enterOrSkipEmployment: toActionObject(skipStep(STEP.EMPLOYMENT)),
  enterOrSkipExtendedBuyOverview: toActionObject(skipStep(STEP.EXTENDED_BUYER_LEADS_OVERVIEW)),
  enterOrSkipTimeHorizon: toActionObject(skipStep(STEP.TIME_HORIZON)),
  enterOrSkipReinforcement: toActionObject(skipStep(STEP.REINFORCEMENT)),
  enterOrSkipPartialSale: toActionObject(skipStep(STEP.PARTIAL_SALE)),
  enterOrSkipOwner: toActionObject(skipStep(STEP.OWNER)),
  enterOrSkipCommercialRealEstateType: toActionObject(skipStep(STEP.COMMERCIAL_REAL_ESTATE_TYPE)),
  enterOrSkipEquity: toActionObject(skipStep(STEP.EQUITY)),
  enterOrSkipIncome: toActionObject(skipStep(STEP.INCOME)),
  enterOrSkipConsultation: toActionObject(skipStep(STEP.CONSULTATION)),
  enterOrSkipPlacementType: toActionObject(skipStep(STEP.PLACEMENT_TYPE)),
  enterOrSkipRentOutIntent: toActionObject(skipStep(STEP.RENT_OUT_INTENT)),
  enterOrSkipRentOutReinforcement: toActionObject(skipStep(STEP.RENT_OUT_REINFORCEMENT)),
  enterOrSkipRadius: toActionObject(skipStep(STEP.RADIUS)),

  updateContextFromQueryParam: toActionObject(
    pure(() =>
      assign({
        skippedSteps: (ctx: FlowMachineContext) => getFlowSkippedSteps(ctx),
      })
    )
  ),
  nextAction: toActionObject(pure(() => raise(ACTION.NEXT))),
  updateRichReportSent: toActionObject(
    pure(() =>
      assign({
        richReportSent: true,
      })
    )
  ),
  sendRichEvent: sendRichTracking(),
}
